import React, { useEffect, useState } from "react";
import { EmptyState } from "../../../../components/recharts/ReMixCharts/components/EmptyState";
import { SheetTableSkeleton } from "./SheetTableSkeleton";

interface PeriodMetrics {
  demand: number | null;
  availability: number;
  banking: number;
  overhauling: number;
  final_availability: number;
  surplus_shortage: number | null;
}

interface PeriodDef {
  from: string;
  to: string;
}

interface PeriodRow {
  block?: number;
  hour?: number;
  time: string;
  periods: PeriodMetrics[];
}

interface PeriodData {
  granularity: string;
  periods: PeriodDef[];
  data: PeriodRow[];
}

interface Props {
  date: string;
  fetchFn: (p?: { date?: string }) => Promise<any>;
  onDataLoaded?: (data: any) => void;
}

const TEAL      = "#34656D";
const BORDER    = "1px solid #E2E8F0";
const HDR_BG    = "linear-gradient(#EDF4F5,#EDF4F5)";
const TS_W      = 130;

const METRICS: { key: keyof PeriodMetrics; label: string; w: number }[] = [
  { key: "demand",             label: "Demand",          w: 90  },
  { key: "availability",       label: "Availability",    w: 100 },
  { key: "banking",            label: "Banking",         w: 90  },
  { key: "overhauling",        label: "Overhauling",     w: 100 },
  { key: "final_availability", label: "Final Avail.",    w: 96  },
  { key: "surplus_shortage",   label: "Sur. / Short.",   w: 104 },
];

function formatPeriod(p: PeriodDef): string {
  const opts: Intl.DateTimeFormatOptions = { day: "2-digit", month: "short" };
  const from = new Date(p.from).toLocaleDateString("en-GB", opts);
  const to   = new Date(p.to).toLocaleDateString("en-GB", opts);
  return `${from} – ${to}`;
}

// Alternate period group backgrounds to break up the density
const GROUP_BG = ["#ffffff", "#f5fbfc"];

export const PeriodReportTable: React.FC<Props> = ({ date, fetchFn, onDataLoaded }) => {
  const [data, setData] = useState<PeriodData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!date) return;
    setLoading(true);
    fetchFn({ date })
      .then((d) => { setData(d); onDataLoaded?.(d); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [date, fetchFn]);

  if (loading) return <SheetTableSkeleton cols={6} />;

  if (!data || !data.data || data.data.length === 0) {
    return (
      <div className="flex-1 min-h-0 relative bg-white rounded-xl" style={{ border: BORDER, minHeight: 200 }}>
        <EmptyState />
      </div>
    );
  }

  const { periods, data: rows, granularity } = data;

  return (
    <div className="flex-1 min-h-0 rounded-xl overflow-hidden bg-white flex flex-col mt-2"
      style={{ border: BORDER, boxShadow: "0 2px 12px rgba(52,101,109,0.08)" }}>
      <div style={{ overflowX: "auto", overflowY: "auto", flex: 1, minHeight: 0 }}>
        <table style={{ borderCollapse: "separate", borderSpacing: 0, fontSize: 12, minWidth: "100%" }}>
          <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>

            {/* ── Row 1: period labels ── */}
            <tr>
              <th rowSpan={2} style={{
                position: "sticky", top: 0, left: 0, zIndex: 22,
                backgroundImage: HDR_BG, color: TEAL, fontWeight: 700, fontSize: 13,
                minWidth: TS_W,
                border: BORDER, borderRight: BORDER, borderBottom: BORDER,
                boxShadow: "3px 0 6px rgba(52,101,109,0.12)",
                textAlign: "center", verticalAlign: "middle",
              }}>
                Time Slot
              </th>
              {periods.map((p, pi) => (
                <th key={pi} colSpan={METRICS.length} style={{
                  backgroundImage: HDR_BG,
                  color: TEAL, fontWeight: 700, fontSize: 12,
                  border: BORDER,
                  borderBottom: "1px solid #CAD5E2",
                  textAlign: "center", padding: "10px 8px",
                  position: "sticky", top: 0, zIndex: 10,
                  whiteSpace: "nowrap", letterSpacing: "0.01em",
                }}>
                  {formatPeriod(p)}
                </th>
              ))}
            </tr>

            {/* ── Row 2: metric sub-headers ── */}
            <tr>
              {periods.flatMap((_, pi) =>
                METRICS.map(({ label, w }, mi) => {
                  const isSS = mi === METRICS.length - 1;
                  return (
                    <th key={`${pi}-${mi}`} style={{
                      backgroundImage: HDR_BG,
                      color: TEAL, fontWeight: 500, fontSize: 11,
                      border: BORDER,
                      borderBottom: "2px solid #CAD5E2",
                      textAlign: "center", minWidth: w, padding: "6px 4px",
                      position: "sticky", top: 39, zIndex: 9,
                      whiteSpace: "nowrap",
                      fontStyle: isSS ? "italic" : "normal",
                    }}>
                      {label}
                    </th>
                  );
                })
              )}
            </tr>
          </thead>

          <tbody>
            {rows.map((row, idx) => {
              const even  = idx % 2 === 0;
              const rowBg = even ? "#FFFFFF" : "#F7FAFB";
              return (
                <tr key={row.block ?? row.hour} style={{ backgroundColor: rowBg }}>

                  {/* sticky time slot */}
                  <td style={{
                    position: "sticky", left: 0, zIndex: 5,
                    backgroundColor: rowBg, color: TEAL, fontWeight: 500, fontSize: 12,
                    height: 36, minWidth: TS_W, whiteSpace: "nowrap",
                    border: BORDER, borderRight: BORDER,
                    boxShadow: "3px 0 6px rgba(52,101,109,0.08)",
                    textAlign: "center", verticalAlign: "middle",
                  }}>
                    {row.time}
                  </td>

                  {row.periods.flatMap((pm, pi) =>
                    METRICS.map(({ key, w }, mi) => {
                      const val   = pm[key] as number | null;
                      const isSS  = key === "surplus_shortage";
                      const color = val === null
                        ? "#D4D4D8"
                        : isSS
                          ? val >= 0 ? "#059669" : "#DC2626"
                          : "#374151";

                      return (
                        <td key={`${pi}-${mi}`} style={{
                          border: BORDER,
                          textAlign: "center", verticalAlign: "middle",
                          fontSize: 12, padding: "0 6px",
                          height: 36, minWidth: w,
                          color,
                          fontWeight: isSS && val !== null ? 600 : 400,
                        }}>
                          {val === null ? "–" : val === 0 ? "0" : val.toFixed(2)}
                        </td>
                      );
                    })
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
